import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { REPORT_CONSTANTS } from "~/helpers/constants";
import { useReportCRUD } from "~/hooks/useReportCRUD";
import { useUserCRUD } from "~/hooks/useUserCRUD";
import { useUserData } from "~/store/userStore";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { AUTH_STORE_INSTANCE } from "~/store/auth";
import { usePasswordCheck } from "~/hooks/usePasswordCheck";
import { REPORT_DETAIL_STORE_INSTANCE } from "~/store/details";
import { useReportDetailStore } from "~/store/details/store";
import { useReportListActions } from "~/store/reportListStore";

export function PendingReportSockets() {
  const { addNewReportMutation, invalidateCurrentFolderNoFilters, updateResumeMutation, invalidateSummary } = useReportCRUD(null, false);
  const { invalidateUser: invalidateUserData } = useUserCRUD();
  const userData = useUserData();
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const {
    selectors: { useIsAuthorized },
  } = AUTH_STORE_INSTANCE;
  const isUserAuthorized = useIsAuthorized();
  const { withPasswordCheck } = usePasswordCheck();
  const { setSummaryData, setSummaryStatus, setIsGeneratingSummary } =
    REPORT_DETAIL_STORE_INSTANCE.selectors.useReportDetailActions();
  const { updateItemById } = useReportListActions();

  const connect = useCallback(() => {
    if (!userData?.user_id || !isUserAuthorized) return;
    wsRef.current?.close();

    const userId = userData.user_id;
    const url = import.meta.env.DEV
      ? `ws://localhost:8000/reports/ws/snap-status/${userId}`
      : `wss://${window.location.host}/reports/ws/snap-status/${userId}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = async () => {
      console.log(`[PendingReportSockets] - Conexão WebSocket aberta para ${url}`);
    };

    ws.onmessage = (evt) => {
      let payload;
      try {
        payload = JSON.parse(evt.data);
      } catch (error) {
        console.error("Erro ao parsear mensagem WebSocket:", error);
        return;
      }

      const messageType = payload.message_type;
      const status = payload[REPORT_CONSTANTS.report_status_message.status];
      const { completed, error } = REPORT_CONSTANTS.status;

      if (messageType === "summary") {
        const reportId = payload.id;
        const eventSummaryId: string | undefined = payload.summary_id ?? payload.result?.summary_id;

        // Verifica se o relatório exibido em tela corresponde ao resumo recebido
        const currentMetadata = useReportDetailStore.getState().data.metadata;
        const currentReportId = currentMetadata?.user_reports_id;
        const isCurrentReport = currentReportId === reportId;

        // Idempotência: descarta eventos de gerações antigas (summary_id não bate
        // com o registro corrente em cache). Evita "mostra erro e depois aparece
        // sumário" quando o usuário regera durante uma geração em vôo.
        const cachedSummary = queryClient.getQueryData<any>(["reports", "summary", reportId]);
        const cachedSummaryId: string | undefined = cachedSummary?.summary_id;
        if (eventSummaryId && cachedSummaryId && eventSummaryId !== cachedSummaryId) {
          console.warn(
            `[PendingReportSockets] Ignorando evento de geração obsoleta: ` +
            `eventSummaryId=${eventSummaryId}, cachedSummaryId=${cachedSummaryId}, reportId=${reportId}`
          );
          return;
        }

        // Atualiza Zustand store + React Query cache da listagem
        const updateReportListCache = (hasSummary: boolean, summaryGenerating: boolean) => {
          const fields = { has_summary: hasSummary, summary_generating: summaryGenerating } as any;
          updateItemById(reportId, fields);
          queryClient.setQueriesData<any>(
            { queryKey: ["reports", "list"], exact: false },
            (oldData: any) => {
              if (!oldData?.data) return oldData;
              return {
                ...oldData,
                data: oldData.data.map((item: any) => {
                  if (item?.user_reports_id === reportId) {
                    return { ...item, ...fields };
                  }
                  return item;
                }),
              };
            }
          );
        };

        // Ignora mensagens intermediárias (started, section_completed, global_processing)
        // Atualiza a lista apenas em status terminal (success/error)
        if (status !== completed && status !== error) {
          return;
        }

        if (status === error) {
          if (isCurrentReport) {
            setSummaryStatus("error");
            setIsGeneratingSummary(false);
          }

          // Atualiza cache da lista: registro do sumário ainda existe no banco, badge permanece visível
          updateReportListCache(true, false);

          const description = payload.result?.message || "Ocorreu um erro ao tentar gerar o sumário. Tente novamente.";
          toast.error("Erro ao gerar sumário", { description });
          withPasswordCheck(() => {
            invalidateSummary(reportId);
          });
          return;
        }

        if (status === completed) {
          const summaryData = payload.result?.data;
          if (summaryData) {
            if (isCurrentReport) {
              setSummaryStatus("pending");
            }

            // Atualiza cache da lista: sumário pronto
            updateReportListCache(true, false);

            const updatePayload = {
              summary_status: "success" as const,
              data: summaryData,
            };

            updateResumeMutation.mutate(
              {
                reportId,
                summaryPayload: updatePayload as any,
                report: queryClient.getQueryData<any>(["reports", "details", reportId]),
              },
              {
                onSuccess: () => {
                  // Atualiza a store apenas se o relatório em tela for o mesmo do resumo
                  if (isCurrentReport) {
                    setSummaryData(summaryData);
                    setSummaryStatus("success");
                    setIsGeneratingSummary(false);
                  }
                  toast.success("Sumário Executivo gerado com sucesso!", {
                    description: "Seu sumário está pronto para ser visualizado.",
                  });
                  withPasswordCheck(() => {
                    invalidateSummary(reportId);
                  });
                },
                onError: () => {
                  if (isCurrentReport) {
                    setSummaryStatus("error");
                    setIsGeneratingSummary(false);
                  }
                  toast.error("Erro ao salvar sumário", {
                    description: "Ocorreu um erro ao tentar salvar o sumário.",
                  });
                },
              }
            );
          }
        }
        return;
      }

      if (status === error) {
        const currentFolderId = window.location.pathname.startsWith('/pasta/')
          ? window.location.pathname.split('/pasta/')[1]
          : null;
        toast.error("Erro ao gerar relatório", {
          description: "Ocorreu um erro ao tentar gerar o relatório. Tente novamente.",
        });
        withPasswordCheck(() => {
          invalidateCurrentFolderNoFilters(currentFolderId);
        });
      }

      if (status === completed) {
        const newReport = payload.result?.data;
        if (newReport) {
          addNewReportMutation.mutate(newReport, {
            onSuccess: () => {
              const currentFolderId = window.location.pathname.startsWith('/pasta/')
                ? window.location.pathname.split('/pasta/')[1]
                : null;
              toast.success("Relatório gerado com sucesso!", {
                description: "Seu relatório está pronto para ser acessado.",
              });
              withPasswordCheck(() => {
                invalidateUserData();
                invalidateCurrentFolderNoFilters(currentFolderId);
              });
            },
            onError: createErrorHandler("Erro ao tentar salvar o novo relatório", "Erro ao salvar"),
          });
        }
      }
    };
    ws.onerror = (e) => {
      console.log("Erro na conexão WebSocket:", e)
    };
    ws.onclose = (e) => console.log("Conexão WebSocket fechada:", e);
  }, [userData, isUserAuthorized]);

  // Conectar ao renderizar e desconectar ao desmontar
  useEffect(() => {
    connect();
    return () => {
      wsRef.current?.close();
    };
  }, [connect]);

  //evento global para reconectar o websocket após a criação de um novo relatório
  useEffect(() => {
    const handler = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        connect();
      }
    };
    window.addEventListener('snap:report-created', handler);
    return () => window.removeEventListener('snap:report-created', handler);
  }, [connect]);

  return null;
}